import { ReactElement, MouseEvent, useMemo, useCallback } from 'react';
import { Stack, Button } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt as ExternalLinkAltIcon } from '@fortawesome/free-solid-svg-icons';

import { useAppSelector } from '~hooks/useAppSelector';
import { selectAuth } from '~store/slices/auth';
import currentTheme from '~config/theme';
import { toAbsoluteUrl } from '~utils/assetsHelpers';
import { getUrlTrackingCompany, openUrlInNewTab } from '~utils/commons';

import {
  DashboardVariantV2GiverStatusInfoWidgetCard,
  DashboardVariantV2GiverInfoCurrentCarrierCompany,
} from '../../SubComponents';

import { useDashboardVariantV2GiverContext } from '../../../Context';
import { DashboardVariantV2GiverContextTabId } from '../../../types';

const WIDGET_TAB_ID: DashboardVariantV2GiverContextTabId = 'inTransit';
const WIDGET_ACCENT_COLOR = currentTheme.palette.primary.main;

const DashboardVariantV2GiverInTransitWidget = (): ReactElement => {
  const { user } = useAppSelector(selectAuth);

  const {
    activeTab: { getIsActiveTab, setActiveTab },
    statistics: { data: statistics },
  } = useDashboardVariantV2GiverContext();

  const isSelected = useMemo(
    () => getIsActiveTab(WIDGET_TAB_ID),
    [getIsActiveTab],
  );

  const handleSelectedTab = useCallback(() => {
    setActiveTab(WIDGET_TAB_ID);
  }, [setActiveTab]);

  const handleRedirectTrackingPage = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      const trackingUrl = getUrlTrackingCompany(user?.companyId ?? '');

      openUrlInNewTab(trackingUrl);
    },
    [user?.companyId],
  );

  const carrierCompanyInfo = useMemo(() => {
    if (!statistics.carrierCompany) return undefined;

    return (
      <DashboardVariantV2GiverInfoCurrentCarrierCompany
        name={statistics.carrierCompany.name}
        logoUrl={statistics.carrierCompany.logoFile?.url}
        email={statistics.carrierCompany.contactEmail}
        numberPhone={
          statistics.carrierCompany.contactMobile ??
          statistics.carrierCompany.contactPhone
        }
      />
    );
  }, [statistics?.carrierCompany]);

  return (
    <DashboardVariantV2GiverStatusInfoWidgetCard
      accentColor={WIDGET_ACCENT_COLOR}
      isSelected={isSelected}
      onSelected={handleSelectedTab}
      header={{
        statistics: {
          current: statistics.inTransitItems,
          total: statistics.totalItems,
        },
        icon: {
          src: toAbsoluteUrl('/icons/icon_section_transit.svg'),
          alt: 'Section Transit Icon',
        },
        title: {
          text: 'EN TRÁNSITO',
          tooltip: 'Pedidos que se encuentran en camino a su destino.',
        },
      }}
      content={
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Button
            variant="text"
            color="secondary"
            startIcon={
              <FontAwesomeIcon
                icon={ExternalLinkAltIcon}
                color={currentTheme.palette.primary.main}
              />
            }
            onClick={handleRedirectTrackingPage}
          >
            Ver página de seguimiento
          </Button>
        </Stack>
      }
      info={carrierCompanyInfo}
    />
  );
};

export default DashboardVariantV2GiverInTransitWidget;
